import styled from 'styled-components';
import { baseTheme } from '../../../../../../global/styles/theme';

export const AgreementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .agreement-header {
    .agreement-step {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 10px;
      background: #fef3c7;
      font-size: 13px;
      font-weight: 700;
      color: ${baseTheme.colors.warning};
      margin-bottom: 6px;
    }

    .agreement-title {
      font-size: 20px;
      font-weight: 700;
      color: ${baseTheme.colors.primary};
    }

    .agreement-subtitle {
      margin-top: 4px;
      font-size: 14px;
      color: ${baseTheme.colors.secondary};
    }
  }

  .ok-summary {
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 12px;
    border: 1px solid #bbf7d0;
    background: #f0fdf4;
    font-size: 14px;
    font-weight: 600;
    color: ${baseTheme.colors.focus};
  }
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid ${baseTheme.colors.stroking};
  background: ${baseTheme.colors.white};

  .section-header {
    display: flex;
    align-items: center;
    gap: 10px;

    .section-number {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: ${baseTheme.colors.mainLightBlue};
      color: ${baseTheme.colors.white};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: 700;
      flex-shrink: 0;
    }

    .section-title {
      font-size: 16px;
      font-weight: 700;
      color: ${baseTheme.colors.primary};
    }
  }
`;

export const CallBlock = styled.div<{ done: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid
    ${({ done }) => (done ? '#86efac' : baseTheme.colors.stroking)};
  background: ${({ done }) => (done ? '#f0fdf4' : baseTheme.colors.background)};

  .call-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
  }

  .call-info {
    .call-title {
      font-size: 15px;
      font-weight: 700;
      color: ${baseTheme.colors.primary};
    }

    .call-sub {
      margin-top: 2px;
      font-size: 12px;
      color: ${baseTheme.colors.secondary};
    }

    .call-phone {
      margin-top: 4px;
      font-size: 18px;
      font-weight: 700;
      color: ${baseTheme.colors.mainLightBlue};

      a {
        color: inherit;
        text-decoration: none;
      }
    }
  }

  .call-button {
    padding: 10px 22px;
    border-radius: 12px;
    border: 2px solid ${baseTheme.colors.focus};
    background: ${baseTheme.colors.white};
    font-size: 14px;
    font-weight: 700;
    color: ${baseTheme.colors.focus};
    cursor: pointer;
    white-space: nowrap;

    &.done {
      border: none;
      background: ${baseTheme.colors.focus};
      color: ${baseTheme.colors.white};
      cursor: default;
    }
  }

  .call-script {
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid #bbf7d0;
    background: #f0fdf4;

    .script-title {
      font-size: 13px;
      font-weight: 700;
      color: ${baseTheme.colors.focus};
      margin-bottom: 6px;
    }

    .script-text {
      font-size: 14px;
      line-height: 1.6;
      color: ${baseTheme.colors.primary};
    }
  }

  .call-hint {
    font-size: 13px;
    line-height: 1.4;
    color: ${baseTheme.colors.error};
  }
`;

export const ProblemGroup = styled.div<{
  groupColor: string;
  groupBackground: string;
  groupBorder: string;
}>`
  border-radius: 12px;
  border: 1px solid ${({ groupBorder }) => groupBorder};
  background: ${({ groupBackground }) => groupBackground};
  padding: 14px;

  .group-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;

    .group-title {
      font-size: 15px;
      font-weight: 700;
      color: ${({ groupColor }) => groupColor};
    }

    .group-count {
      padding: 1px 9px;
      border-radius: 50px;
      background: ${({ groupColor }) => groupColor};
      color: ${baseTheme.colors.white};
      font-size: 13px;
      font-weight: 700;
    }
  }

  .group-hint {
    font-size: 13px;
    color: ${baseTheme.colors.secondary};
    margin-bottom: 10px;
  }

  .group-items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

export const ProblemItem = styled.div<{ itemColor: string }>`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${baseTheme.colors.stroking};
  background: ${baseTheme.colors.white};

  .item-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;

    .item-name {
      font-size: 14px;
      font-weight: 700;
      color: ${baseTheme.colors.primary};

      &.removed {
        color: ${baseTheme.colors.tertiary};
        text-decoration: line-through;
      }
    }

    .item-quantity {
      font-size: 14px;
      font-weight: 700;
      color: ${({ itemColor }) => itemColor};
      white-space: nowrap;
    }
  }

  .item-detail {
    margin-top: 6px;
    display: inline-block;
    padding: 4px 10px;
    border-radius: 8px;
    background: ${baseTheme.colors.background};
    font-size: 13px;
    font-weight: 600;
    color: ${({ itemColor }) => itemColor};
  }

  .decision-buttons {
    display: flex;
    gap: 8px;
    margin-top: 10px;

    button {
      flex: 1;
      padding: 10px 8px;
      border-radius: 10px;
      border: 1px solid ${baseTheme.colors.stroking};
      background: ${baseTheme.colors.white};
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;

      &.keep {
        border-color: ${baseTheme.colors.focus};
        color: ${baseTheme.colors.focus};
      }

      &.remove {
        border-color: ${baseTheme.colors.error};
        color: ${baseTheme.colors.error};
      }
    }
  }

  .decision-result {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px dashed ${baseTheme.colors.focus};
    background: #f0fdf4;
    font-size: 13px;
    font-weight: 600;
    color: ${baseTheme.colors.focus};

    .reset-button {
      background: none;
      border: none;
      padding: 0;
      font-size: 13px;
      color: ${baseTheme.colors.tertiary};
      cursor: pointer;
      text-decoration: underline;
      white-space: nowrap;
    }
  }
`;

export const AgreementFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid ${baseTheme.colors.stroking};

  .footer-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    font-size: 14px;
    color: ${baseTheme.colors.secondary};

    .total-amount {
      font-size: 20px;
      font-weight: 700;
      color: ${baseTheme.colors.primary};
    }
  }

  .empty-order-hint {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid ${baseTheme.colors.error};
    background: #fef2f2;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.4;
    color: ${baseTheme.colors.error};

    &::before {
      content: '⚠️';
      font-size: 18px;
      line-height: 1.2;
    }
  }

  .footer-actions {
    display: flex;
    gap: 12px;

    & > div:last-child {
      flex: 1;
    }
  }
`;
